import { appConfig } from '@/config/app.config';
import { getErrorMessage } from '@/utils/error.utils';
import ErrorImage from '@assets/images/app-error-logo.png';
import { Typography } from '@components/ui/Typography';
import { Button } from '@components/ui/Button';

interface ErrorFallbackProps {
  error: unknown;
  /** Called when the user wants to retry — compatible with both react-error-boundary and TanStack Router */
  resetErrorBoundary: () => void;
  /**
   * When true, forces the error details section to be shown regardless of the environment.
   * Useful for Storybook stories without mutating the global appConfig.
   */
  showDetails?: boolean;
}

/**
 * Unified Error Fallback Component.
 * Can be used as:
 * - react-error-boundary `FallbackComponent` (receives `{ error, resetErrorBoundary }`)
 * - TanStack Router `errorComponent` adapter (maps `reset` to `resetErrorBoundary`)
 */
export function ErrorFallback({ error, resetErrorBoundary, showDetails }: ErrorFallbackProps) {
  const message = getErrorMessage(error);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center max-w-md mx-auto p-6">
        <div className="w-3/4 mx-auto mb-4">
          <img src={ErrorImage} alt="Error Image" className="mx-auto w-full max-w-sm h-auto" />
        </div>
        <Typography variant="title-small" weight="bold" className="mb-4">
          Something went wrong
        </Typography>
        <Typography className="text-muted-foreground mb-6">{message}</Typography>
        <div className="space-y-3">
          <Button onClick={resetErrorBoundary} variant="default" className="w-full">
            Try Again
          </Button>
          <Button
            onClick={() => window.location.reload()}
            variant="ghost"
            className="w-full hover:bg-transparent"
          >
            Reload Page
          </Button>
        </div>
        {(showDetails || appConfig.isDevelopment) && error instanceof Error && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-sm text-muted-foreground">
              Error Details (Development)
            </summary>
            <Typography
              variant="body-small"
              className="whitespace-pre-line mt-2 bg-muted p-3 rounded overflow-auto"
            >
              {error.stack}
            </Typography>
          </details>
        )}
      </div>
    </div>
  );
}
